import type { ContactColumnId } from "@/interfaces/adminContacts";

/**
 * Column id -> header label. Recipient ("name") is fixed -- always first, never configurable,
 * never hidden (PRD section 4 item 6) -- but still gets a label here for CSV headers.
 */
export const COLUMN_LABELS: Record<ContactColumnId, string> = {
  name: "Recipient",
  email: "Recipient Email",
  organization: "Organization",
  campaign: "Campaign",
  status: "Status",
  account_lead: "Account Lead",
  client_name: "Client Name",
  client_email: "Client Email",
  redemption_link: "Redemption Link",
  delivered: "Delivered",
  campaign_count: "Total Campaigns",
};

/**
 * Default arrangement of the *configurable* columns -- everything except the fixed "name"
 * (Recipient) column, which `buildColumns` always prepends separately. This list enumerates
 * every configurable column id, so it doubles as the full `allIds` set `useColumnPrefs` needs.
 */
export const DEFAULT_ORDER: ContactColumnId[] = [
  "email",
  "organization",
  "campaign",
  "status",
  "account_lead",
  "client_name",
  "client_email",
  "redemption_link",
  "delivered",
  "campaign_count",
];

/** Hidden by default until the user opts in via the Columns popover (PRD section 4 item 6). */
export const DEFAULT_HIDDEN: ContactColumnId[] = ["delivered", "campaign_count"];

/**
 * URL search param that holds the column layout (decision D3) -- no localStorage, no Clerk
 * metadata; the arrangement lives only in `?cols=` and is shareable via the URL.
 */
export const COLS_PARAM = "cols";

/** Rows per page (ticket-specified; the Blotter's own default PAGE_LIMIT is 20). */
export const PAGE_SIZE = 100;

/** Table minimum width before horizontal scroll (prototype's `.ga-contacts-table-wrap table`). */
export const MIN_WIDTH = 1100;

/** Placeholder the table and the CSV use for a value the row does not have. */
export const EMPTY_CELL = "—";

/**
 * The screen's user-facing strings, in one place so a wording change is one edit.
 *
 * Status labels are NOT here: they belong to the status palette
 * (`STATUS_VISUALS` in `constants/campaings.ts`), which is the single source of truth for both
 * what a status is called and how it is drawn.
 */
export const ADMIN_CONTACTS_TEXT = {
  searchPlaceholder: "Search and filter contacts",
  itemNoun: "contact",
  csvFilename: "gfg-contacts.csv",
  copyToast: "Redemption link copied to clipboard",
  popoverHint: "Recipient is always shown.",
} as const;
